import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@/contexts/SharedAuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useCreateTicket } from '@/hooks/useTickets';
import { calculatePriority, FORM_ASSIGNMENTS } from '../../../../../shared/form-config';
import { useTranslation } from 'react-i18next';
import { useFileValidation } from '@/hooks/useFileValidation';
import { ReporterInfoFields } from '@/components/tickets/ReporterInfoFields';
import { TicketFormButtons } from '@/components/tickets/TicketFormButtons';

const accessSchema = z.object({
  summary: z.string().min(5, 'El resumen debe tener al menos 5 caracteres'),
  description: z.string().min(20, 'La descripción debe tener al menos 20 caracteres'),
  accessType: z.enum(['internal', 'external', 'both'], {
    required_error: 'Debe seleccionar un tipo de acceso',
  }),
  clientName: z.string().optional(),
  internalEmails: z.string().optional(),
  externalEmails: z.string().optional(),
});

type AccessFormData = z.infer<typeof accessSchema>;

export function AccessForm() {
  const { user } = useAuth();
  const { file, handleFileChange, removeFile } = useFileValidation();
  const { t } = useTranslation();

  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<AccessFormData>({
    resolver: zodResolver(accessSchema),
  });

  const accessType = watch('accessType');

  // ✨ Using tRPC mutation
  const createTicketMutation = useCreateTicket();

  const onSubmit = async (data: AccessFormData) => {
    const priority = calculatePriority('access', data);
    const ticketData = {
      formType: 'access',
      title: `Accesos: ${data.summary}`,
      description: data.description,
      formData: {
        reporterName: user?.name,
        reporterEmail: user?.email,
        ...data,
      },
      attachments: file ? [file] : [],
      priority,
      assignedTo: FORM_ASSIGNMENTS['access'],
    };
    createTicketMutation.mutate(ticketData as any);
  };

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle>{t('forms.accessTitle')}</CardTitle>
        <CardDescription>
          {t('tickets.accessform.completa_el_formulario_para_solicitar_accesos')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Summary */}
          <div>
            <Label htmlFor="summary">{t('forms.summary')} *</Label>
            <Input
              id="summary"
              {...register('summary')}
              placeholder={t('tickets.accessform.ej_acceso_a_plataforma_x_para')}
            />
            {errors.summary && (
              <p className="text-sm text-red-500 mt-1">{errors.summary.message}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="description">{t('forms.description')} *</Label>
            <Textarea
              id="description"
              {...register('description')}
              placeholder={t('tickets.accessform.detalla_los_accesos_necesarios_y_la')}
              rows={4}
            />
            {errors.description && (
              <p className="text-sm text-red-500 mt-1">{errors.description.message}</p>
            )}
          </div>

          {/* Access Type */}
          <div>
            <Label>{t('forms.accessType')} *</Label>
            <RadioGroup
              onValueChange={(value) => setValue('accessType', value as any)}
              className="mt-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="internal" id="internal" />
                <Label htmlFor="internal" className="font-normal cursor-pointer">
                  {t('forms.accessInternal')}
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="external" id="external" />
                <Label htmlFor="external" className="font-normal cursor-pointer">
                  {t('forms.accessExternal')}
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="both" id="both" />
                <Label htmlFor="both" className="font-normal cursor-pointer">
                  {t('forms.accessBoth')}
                </Label>
              </div>
            </RadioGroup>
            {errors.accessType && (
              <p className="text-sm text-red-500 mt-1">{errors.accessType.message}</p>
            )}
          </div>

          {/* Conditional Fields */}
          {accessType === 'external' || accessType === 'both' ? (
            <div>
              <Label htmlFor="clientName">{t('forms.clientNameAccess')}</Label>
              <Input
                id="clientName"
                {...register('clientName')}
                placeholder={t('tickets.accessform.nombre_del_cliente_o_partner')}
              />
            </div>
          ) : null}

          {accessType === 'internal' || accessType === 'both' ? (
            <div>
              <Label htmlFor="internalEmails">{t('forms.internalEmails')}</Label>
              <Textarea
                id="internalEmails"
                {...register('internalEmails')}
                placeholder={t('forms.internalEmailsPlaceholder')}
                rows={3}
              />
            </div>
          ) : null}

          {accessType === 'external' || accessType === 'both' ? (
            <div>
              <Label htmlFor="externalEmails">{t('forms.externalEmails')}</Label>
              <Textarea
                id="externalEmails"
                {...register('externalEmails')}
                placeholder={t('forms.externalEmailsPlaceholder')}
                rows={3}
              />
            </div>
          ) : null}

          {/* File Upload */}
          <div>
            <Label htmlFor="file">{t('forms.attachmentOptional')}</Label>
            <Input
              id="file"
              type="file"
              onChange={handleFileChange}
              className="mt-1"
            />
            {file && (
              <div className="mt-2 flex items-center justify-between p-2 bg-gray-100 rounded">
                <span className="text-sm">{file.name}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={removeFile}
                >
                  {t('forms.delete')}
                </Button>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-1">
              {t('tickets.accessform.maximo_5mb_formatos_pdf_word_excel')}
            </p>
          </div>

          <ReporterInfoFields name={user?.name} email={user?.email} />

          <TicketFormButtons isPending={createTicketMutation.isPending} />
        </form>
      </CardContent>
    </Card>
  );
}
